import { useCallback, useEffect, useState } from "react";
import { Redirect, Route, Router, Switch } from "react-router-dom";
import { useToasterStore } from "react-hot-toast";
import { useSelector } from "react-redux";

import {
  clearCreatedTableRowsIds,
  filterActiveNotifications,
  resetAppliedFilters,
  resetCognitoUser,
  resetSelectedFilters,
  setIsLogout
} from "../state/ducks";
import { selectCreatedTableRowsIds } from "../state/selectors";
import { dispatch, getState } from "../state/store";

import { routePaths } from "../consts";
import { historyService, navigationService, awsAmplifyService, authService } from "../services";
import { globalUtil } from "../utils";

// import { DarkModeSwitch } from "../components";
import { IdleDetector, AppVersionDetector, NavBar, PrivateRoute } from "../containers";
import {
  UserManagementPage,
  CreateUserPage,
  EditUserPage,
  ConsentManagementPage,
  ConsentElementsPage,
  CreateConsentElementPage,
  EditConsentElementPage,
  MarketManagementPage,
  AppsManagementPage,
  NotificationManagementPage,
  CreateNotificationPage,
  EditNotificationPage,
  LoginPage,
  FleetManagementPage,
  CreateFwVersionPage,
  SmartDashboardsPage,
  EditFwVersionPage,
  HardwarePage,
  UserInsightsPage
} from "../pages";
import AppsConfigurationPage from "../pages/AppsManagementPage/AppsConfigurationPage";
import { useNavigationDetector } from "../hooks";

const preventNavigationRoutes = [
  routePaths.USERS_CREATE,
  routePaths.USERS_CREATE_ADMIN,
  routePaths.USERS_EDIT,
  routePaths.CREATE_APP,
  routePaths.CONSENTS,
  routePaths.CONSENTS_CONSENT_ELEMENTS,
  routePaths.CONSENT_ELEMENT_CREATE,
  routePaths.CONSENT_ELEMENT_EDIT,
  routePaths.NOTIFICATIONS_CREATE,
  routePaths.NOTIFICATIONS_EDIT
];

const Main = () => {
  const [isLoggedIn, setIsLoggedId] = useState(false);
  const cognitoUser = useSelector((store) => store.auth.cognitoUser);
  const isMandatoryDataFetched = useSelector((store) => store.auth.isMandatoryDataFetched);

  const { toasts } = useToasterStore();

  useEffect(() => {
    const activeNotificationIds = toasts?.map((t) => t.id);
    dispatch(filterActiveNotifications(activeNotificationIds));
  }, [toasts]);

  const removeCreatedTableRows = useCallback((event) => {
    event.stopPropagation();
    const createdTableRowsIds = selectCreatedTableRowsIds()(getState());

    if (createdTableRowsIds.length) {
      dispatch(clearCreatedTableRowsIds());
    }
  }, []);

  useEffect(() => {
    window.addEventListener("click", removeCreatedTableRows);

    return () => window.removeEventListener("click", removeCreatedTableRows);
  }, []);

  useEffect(async () => {
    try {
      const user = await awsAmplifyService.currentAuthenticatedUser();

      setIsLoggedId(Boolean(user && cognitoUser && isMandatoryDataFetched));
    } catch (err) {
      setIsLoggedId(false);
    }
  }, [cognitoUser, isMandatoryDataFetched]);

  useNavigationDetector(preventNavigationRoutes);

  const logOut = async () => {
    await authService.logOut();
    dispatch(setIsLogout(true));
    globalUtil.resetData();
    dispatch(resetSelectedFilters());
    dispatch(resetAppliedFilters());

    dispatch(resetCognitoUser());

    navigationService.replace(`${routePaths.LOGIN}`);
  };

  return (
    <>
      {/* {!isLoggedIn && <DarkModeSwitch />} */}

      <main>
        <Router history={historyService.getHistory()}>
          {isLoggedIn && <NavBar onLogout={logOut} />}
          <Switch>
            <Route path={routePaths.LOGIN}>
              <LoginPage />
            </Route>

            <PrivateRoute
              token={!!cognitoUser && isLoggedIn}
              exact={true}
              parentPath={routePaths.LOGIN}
              path={routePaths.USERS}>
              <UserManagementPage />
            </PrivateRoute>

            <PrivateRoute
              token={!!cognitoUser && isLoggedIn}
              exact={true}
              parentPath={routePaths.LOGIN}
              path={routePaths.USERS_CREATE}>
              <CreateUserPage />
            </PrivateRoute>

            <PrivateRoute
              token={!!cognitoUser && isLoggedIn}
              parentPath={routePaths.LOGIN}
              path={`${routePaths.USERS_EDIT}/:email`}>
              <EditUserPage />
            </PrivateRoute>

            <PrivateRoute
              token={!!cognitoUser && isLoggedIn}
              exact={true}
              parentPath={routePaths.LOGIN}
              path={routePaths.USERS_CREATE_ADMIN}>
              <CreateUserPage isAdminUser />
            </PrivateRoute>

            <PrivateRoute
              token={!!cognitoUser && isLoggedIn}
              parentPath={routePaths.LOGIN}
              path={`${routePaths.USERS_EDIT_ADMIN}/:email`}>
              <EditUserPage isAdminUser />
            </PrivateRoute>

            <PrivateRoute
              token={!!cognitoUser && isLoggedIn}
              exact={true}
              parentPath={routePaths.LOGIN}
              path={routePaths.DASHBOARD}>
              <SmartDashboardsPage />
            </PrivateRoute>

            <PrivateRoute
              token={!!cognitoUser && isLoggedIn}
              exact={true}
              parentPath={routePaths.LOGIN}
              path={`${routePaths.APPS}/:appName${routePaths.CONSENTS}`}>
              <ConsentManagementPage />
            </PrivateRoute>

            <PrivateRoute
              token={!!cognitoUser && isLoggedIn}
              exact={true}
              parentPath={routePaths.LOGIN}
              path={`${routePaths.APPS}/:appName${routePaths.CONSENT_ELEMENT_CREATE}`}>
              <CreateConsentElementPage />
            </PrivateRoute>

            <PrivateRoute
              token={!!cognitoUser && isLoggedIn}
              exact={true}
              parentPath={routePaths.LOGIN}
              path={`${routePaths.APPS}/:appName${routePaths.CONSENTS_CONSENT_ELEMENTS}/:id?`}>
              <ConsentElementsPage />
            </PrivateRoute>

            <PrivateRoute
              token={!!cognitoUser && isLoggedIn}
              exact={true}
              parentPath={routePaths.LOGIN}
              path={`${routePaths.APPS}/:appName${routePaths.CONSENT_ELEMENT_EDIT}/:id`}>
              <EditConsentElementPage />
            </PrivateRoute>

            <PrivateRoute
              token={!!cognitoUser && isLoggedIn}
              exact={true}
              parentPath={routePaths.LOGIN}
              path={routePaths.MARKETS}>
              <MarketManagementPage />
            </PrivateRoute>

            <PrivateRoute
              token={!!cognitoUser && isLoggedIn}
              exact={true}
              parentPath={routePaths.LOGIN}
              path={routePaths.CREATE_APP}>
              <AppsConfigurationPage />
            </PrivateRoute>

            <PrivateRoute
              token={!!cognitoUser && isLoggedIn}
              exact={true}
              parentPath={routePaths.LOGIN}
              path={routePaths.APPS}>
              <AppsManagementPage />
            </PrivateRoute>

            <PrivateRoute
              token={!!cognitoUser && isLoggedIn}
              exact={true}
              parentPath={routePaths.LOGIN}
              path={routePaths.NOTIFICATIONS}>
              <NotificationManagementPage />
            </PrivateRoute>

            <PrivateRoute
              token={!!cognitoUser && isLoggedIn}
              exact={true}
              parentPath={routePaths.LOGIN}
              path={`${routePaths.NOTIFICATIONS_CREATE}/:isSystemNotification?`}>
              <CreateNotificationPage />
            </PrivateRoute>

            <PrivateRoute
              token={!!cognitoUser && isLoggedIn}
              exact={true}
              parentPath={routePaths.LOGIN}
              path={`${routePaths.NOTIFICATIONS_EDIT}/:notificationName?`}>
              <EditNotificationPage />
            </PrivateRoute>

            <PrivateRoute
              token={!!cognitoUser && isLoggedIn}
              exact={true}
              parentPath={routePaths.LOGIN}
              path={routePaths.FLEET_MANAGEMENT}>
              <FleetManagementPage />
            </PrivateRoute>

            <PrivateRoute
              token={!!cognitoUser && isLoggedIn}
              exact={true}
              parentPath={routePaths.LOGIN}
              path={routePaths.FW_VERSION_CREATE}>
              <CreateFwVersionPage />
            </PrivateRoute>

            <PrivateRoute
              token={!!cognitoUser && isLoggedIn}
              exact={true}
              parentPath={routePaths.LOGIN}
              path={`${routePaths.FW_VERSION_EDIT}/:fwuName?`}>
              <EditFwVersionPage />
            </PrivateRoute>

            <PrivateRoute
              token={!!cognitoUser && isLoggedIn}
              exact={true}
              parentPath={routePaths.LOGIN}
              path={routePaths.HARDWARE}>
              <HardwarePage />
            </PrivateRoute>

            <PrivateRoute
              token={!!cognitoUser && isLoggedIn}
              exact={true}
              parentPath={routePaths.LOGIN}
              path={routePaths.USER_INSIGHTS}>
              <UserInsightsPage />
            </PrivateRoute>

            <Route path="*" exact={true} component={() => <Redirect to={routePaths.LOGIN} />} />
          </Switch>

          <IdleDetector onLogout={logOut} />

          <AppVersionDetector />
        </Router>
      </main>
    </>
  );
};

export default Main;
